import { useEffect, useState } from 'react'
import {
    Button, Col, Form, Modal, ProgressBar, Row, Spinner, Tabs, Tab, Badge
} from 'react-bootstrap'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import {
    CREATE_AUTOMATION,
    UPDATE_AUTOMATION,
    CREATE_MESSAGE_TEMPLATE,
    CREATE_AUTOMATION_TEMPLATE,
    DELETE_AUTOMATION_TEMPLATE,
} from '../../../gql/mutations/campaigns'
import { GET_MESSAGE_TEMPLATES, PREVIEW_AUTOMATION_CONTACTS, GET_AUTOMATION_TEMPLATES } from '../../../gql/queries/campaigns'
import { Automation, IMessageTemplate, CampaignChannel, IAutomationTemplate } from '../../../types/campaigns'
import { TemplateStarterModal } from '../../templates/components/TemplateStarterModal'
import { TemplateFormModal } from '../../campaigns/components/TemplateFormModal'
import { TemplateStarter } from '../../../constants/templateStarters'
import { AUTOMATION_STARTERS, AUTOMATION_STARTER_CATEGORIES, AutomationStarter } from '../../../constants/automationStarters'
import { useGetUser } from '../../../store/userStore'
import { isAdministrator } from '../../../helpers'

interface Props {
    show: boolean
    onHide: () => void
    onSaved: () => void
    automation?: Automation | null
}

const TRIGGER_OPTIONS = [
    { value: 'HAPPY_BIRTHDAY', label: '🎂 Happy Birthday', hasConfig: false },
    { value: 'CONDITIONAL_LEAD', label: '🎯 Conditional Action (Leads)', hasConfig: true },
    { value: 'CONDITIONAL_POLICY', label: '🛡️ Conditional Action (Policy/Quote)', hasConfig: true }
]

export const LEAD_CONDITIONS = [
    { value: 'LEAD_DURATION', label: 'Lead Duration' },
    { value: 'LEAD_STAGE', label: 'Lead Stage (pipeline name)' },
]

export const POLICY_CONDITIONS = [
    { value: 'STATE_CHANGE', label: 'State Change + duration in that state' },
    { value: 'CROSS_SELL_AUTO', label: 'Has Auto without Auto Policy + policy duration' },
    { value: 'CROSS_SELL_HOME', label: 'Has Home without Home Policy + policy duration' },
    { value: 'POLICY_COMPLETED', label: 'Completed Policy + duration in that state' },
    { value: 'BY_CARRIER', label: 'By Carrier (Select Carrier)' }
]

interface WizardState {
    // Step 1
    triggerType: string
    triggerConfig: Record<string, any>
    // Step 2
    channel: string
    templateId: string
    // Step 3
    name: string
    description: string
    scope: 'ORGANIZATION' | 'PERSONAL'
    delayDays: number
    sendTime: string   // "HH:MM"
    isActive: boolean
}

const DEFAULT_STATE: WizardState = {
    triggerType: 'HAPPY_BIRTHDAY',
    triggerConfig: {},
    channel: 'EMAIL',
    templateId: '',
    name: '',
    description: '',
    scope: 'PERSONAL',
    delayDays: 0,
    sendTime: '09:00',
    isActive: true,
}

const toSendTime = (hour?: number, minute?: number) => {
    const h = String(hour ?? 9).padStart(2, '0')
    const m = String(minute ?? 0).padStart(2, '0')
    return `${h}:${m}`
}

const parseSendTime = (time: string) => {
    const [h, m] = time.split(':').map(Number)
    return { sendHour: h || 0, sendMinute: m || 0 }
}

export const AutomationWizard = ({ show, onHide, onSaved, automation }: Props) => {
    const [step, setStep] = useState(0)
    const [form, setForm] = useState<WizardState>(DEFAULT_STATE)

    const [starterModalOpen, setStarterModalOpen] = useState(false)
    const [templateModalOpen, setTemplateModalOpen] = useState(false)
    const [selectedStarter, setSelectedStarter] = useState<TemplateStarter | null>(null)
    const [defaultChannel, setDefaultChannel] = useState<CampaignChannel | undefined>(undefined)
    const [activeTab, setActiveTab] = useState('general')
    const [previewTemplate, setPreviewTemplate] = useState<IMessageTemplate | null>(null)

    // Step 0 — template library
    const [libraryCategory, setLibraryCategory] = useState<string>('ALL')
    const [librarySearch, setLibrarySearch] = useState('')
    const [savingTemplateId, setSavingTemplateId] = useState<string | null>(null)
    const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null)

    const permissions = useGetUser((s) => s.user?.permissions ?? [])
    const isAdmin = isAdministrator(permissions)

    const { data: templatesData, loading: loadingTemplates, refetch: refetchTemplates } = useQuery<{
        messageTemplates: IMessageTemplate[]
    }>(GET_MESSAGE_TEMPLATES, {
        skip: !show,
        fetchPolicy: 'cache-and-network',
    })

    const [createAutomation, { loading: creating }] = useMutation(CREATE_AUTOMATION)
    const [updateAutomation, { loading: updating }] = useMutation(UPDATE_AUTOMATION)
    const [createTemplateMutation, { loading: creatingTemplate }] = useMutation(CREATE_MESSAGE_TEMPLATE)

    const [runPreview, { data: previewData, loading: previewLoading }] = useLazyQuery(
        PREVIEW_AUTOMATION_CONTACTS,
        { fetchPolicy: 'network-only' }
    )

    const { data: customTemplatesData, loading: loadingCustomTemplates, refetch: refetchCustomTemplates } = useQuery<{
        automationTemplates: IAutomationTemplate[]
    }>(GET_AUTOMATION_TEMPLATES, { skip: !show, fetchPolicy: 'cache-and-network' })

    const [createAutomationTemplate, { loading: savingCustomTemplate }] = useMutation(CREATE_AUTOMATION_TEMPLATE)
    const [deleteAutomationTemplate] = useMutation(DELETE_AUTOMATION_TEMPLATE)

    const customTemplates = customTemplatesData?.automationTemplates ?? []

    useEffect(() => {
        if (show) {
            if (automation) {
                let triggerConfig = automation.triggerConfig || {}
                // Normalize: GenericScalar can come back as a double-escaped string
                while (typeof triggerConfig === 'string') {
                    try { triggerConfig = JSON.parse(triggerConfig) } catch { triggerConfig = {}; break }
                }
                setForm({
                    triggerType: automation.triggerType,
                    triggerConfig,
                    channel: automation.channel,
                    templateId: automation.template?.id ?? '',
                    name: automation.name,
                    description: automation.description || '',
                    scope: automation.scope || 'PERSONAL',
                    delayDays: automation.delayDays,
                    sendTime: toSendTime(automation.sendHour, automation.sendMinute),
                    isActive: automation.isActive,
                })
                if (['HAPPY_BIRTHDAY'].includes(automation.triggerType)) {
                    setActiveTab('general')
                } else if (['CONDITIONAL_LEAD'].includes(automation.triggerType)) {
                    setActiveTab('leads')
                } else if (['CONDITIONAL_POLICY'].includes(automation.triggerType)) {
                    setActiveTab('policy')
                } else {
                    setActiveTab('general')
                }
            } else {
                setForm(DEFAULT_STATE)
                setActiveTab('general')
            }
            setStep(automation ? 1 : 0)
            setLibraryCategory('ALL')
            setLibrarySearch('')
        }
    }, [show, automation])

    // Fetch eligible count when entering step 3 or when scope changes
    useEffect(() => {
        if (step !== 3) return
        runPreview({
            variables: {
                triggerType: form.triggerType,
                triggerConfig: form.triggerConfig,
                scope: form.scope,
            },
        })
    }, [step, form.scope, form.triggerType, form.triggerConfig]) // eslint-disable-line react-hooks/exhaustive-deps

    const templates = (templatesData?.messageTemplates ?? []).filter(
        (t) => !form.channel || (t.channel as string) === form.channel || (t as any).allChannels?.includes(form.channel)
    )

    const set = (key: keyof WizardState, value: any) =>
        setForm((prev) => ({ ...prev, [key]: value }))

    const handleOpenCreateTemplate = () => {
        const chan = (form.channel as CampaignChannel) || undefined
        setDefaultChannel(chan)
        setStarterModalOpen(true)
    }

    const handleSelectStarter = (starter: TemplateStarter) => {
        setSelectedStarter(starter)
        setDefaultChannel(starter.channel)
        setStarterModalOpen(false)
        setTemplateModalOpen(true)
    }

    const handleStartFromScratch = () => {
        setSelectedStarter(null)
        setStarterModalOpen(false)
        setTemplateModalOpen(true)
    }

    const handleCreateTemplateSubmit = async (payload: any) => {
        try {
            const { data } = await createTemplateMutation({ variables: payload })
            if (data?.createMessageTemplate?.ok) {
                toast.success('Template created')
                setTemplateModalOpen(false)
                setSelectedStarter(null)
                await refetchTemplates()
                if (data.createMessageTemplate.template?.id) {
                    set('templateId', data.createMessageTemplate.template.id)
                }
            } else {
                toast.error(data?.createMessageTemplate?.errors?.[0] || 'Error creating template')
            }
        } catch {
            toast.error('Unable to save template')
        }
    }

    const applyStarterToForm = (starter: AutomationStarter | IAutomationTemplate) => {
        const isStarter = (s: any): s is AutomationStarter => 'id' in s && typeof (s as any).id === 'string' && (s as any).id.startsWith('system-')
        setForm({
            triggerType: starter.triggerType,
            triggerConfig: starter.triggerConfig || {},
            channel: starter.channel as 'EMAIL' | 'SMS',
            templateId: '',
            name: starter.name,
            description: starter.description || '',
            scope: 'PERSONAL',
            delayDays: starter.delayDays,
            sendTime: `${String(starter.sendHour).padStart(2, '0')}:${String(starter.sendMinute).padStart(2, '0')}`,
            isActive: true,
        })
        // Set the correct tab
        if (starter.triggerType === 'HAPPY_BIRTHDAY') setActiveTab('general')
        else if (starter.triggerType === 'CONDITIONAL_LEAD') setActiveTab('leads')
        else if (starter.triggerType === 'CONDITIONAL_POLICY') setActiveTab('policy')
        setStep(1)
    }

    const handleSaveAsCustomTemplate = async (starter: AutomationStarter) => {
        setSavingTemplateId(starter.id)
        try {
            const { data } = await createAutomationTemplate({
                variables: {
                    name: starter.name,
                    description: starter.description,
                    category: starter.category,
                    triggerType: starter.triggerType,
                    triggerConfig: starter.triggerConfig,
                    channel: starter.channel,
                    delayDays: starter.delayDays,
                    sendHour: starter.sendHour,
                    sendMinute: starter.sendMinute,
                },
            })
            if (data?.createAutomationTemplate?.ok) {
                toast.success('Template saved to your library')
                refetchCustomTemplates()
            } else {
                toast.error(data?.createAutomationTemplate?.errors?.[0] || 'Error saving template')
            }
        } catch {
            toast.error('Unable to save template')
        } finally {
            setSavingTemplateId(null)
        }
    }

    const handleDeleteCustomTemplate = async (id: string) => {
        setDeletingTemplateId(id)
        try {
            const { data } = await deleteAutomationTemplate({ variables: { id } })
            if (data?.deleteAutomationTemplate?.ok) {
                toast.success('Template deleted')
                refetchCustomTemplates()
            }
        } catch {
            toast.error('Unable to delete template')
        } finally {
            setDeletingTemplateId(null)
        }
    }

    const filteredSystemStarters = AUTOMATION_STARTERS.filter((s) => {
        const matchesCategory = libraryCategory === 'ALL' || s.category === libraryCategory
        const matchesSearch = !librarySearch || s.name.toLowerCase().includes(librarySearch.toLowerCase()) || s.description.toLowerCase().includes(librarySearch.toLowerCase())
        return matchesCategory && matchesSearch
    })

    const filteredCustomTemplates = customTemplates.filter((t) => {
        const matchesCategory = libraryCategory === 'ALL' || t.category === libraryCategory
        const matchesSearch = !librarySearch || t.name.toLowerCase().includes(librarySearch.toLowerCase()) || t.description.toLowerCase().includes(librarySearch.toLowerCase())
        return matchesCategory && matchesSearch
    })

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast.error('Enter a name for the automation')
            return
        }
        const { sendHour, sendMinute } = parseSendTime(form.sendTime)
        let triggerConfig = form.triggerConfig
        while (typeof triggerConfig === 'string') {
            try { triggerConfig = JSON.parse(triggerConfig) } catch { triggerConfig = {}; break }
        }
        const variables = {
            name: form.name.trim(),
            description: form.description,
            scope: form.scope,
            triggerType: form.triggerType,
            triggerConfig,
            channel: form.channel,
            templateId: form.templateId || null,
            delayDays: form.delayDays,
            sendHour,
            sendMinute,
            isActive: form.isActive,
        }

        try {
            if (automation) {
                const { data } = await updateAutomation({ variables: { id: automation.id, ...variables } })
                if (!data?.updateAutomation?.ok) throw new Error(data?.updateAutomation?.errors?.[0])
                toast.success('Automation updated')
            } else {
                const { data } = await createAutomation({ variables })
                if (!data?.createAutomation?.ok) throw new Error(data?.createAutomation?.errors?.[0])
                toast.success('Automation created')
            }
            onSaved()
            onHide()
        } catch (err: any) {
            toast.error(err?.message || 'Error saving automation')
        }
    }

    const progress = ((step - 1) / 2) * 100
    const eligibleCount = previewData?.previewAutomationContacts?.contactCount

    const triggerLabel = TRIGGER_OPTIONS.find(t => t.value === form.triggerType)?.label ?? form.triggerType
    const selectedTemplate = templates.find(t => t.id === form.templateId)

    return (
        <>
        <Modal show={show} onHide={onHide} size='xl' centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {automation ? '✏️ Edit Automation' : '⚡ New Automation'}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className='p-0'>
              <Row className='g-0' style={{ minHeight: 420 }}>
                {/* ── Sequence Preview Panel — hidden on step 0 ── */}
                <Col
                  md={step === 0 ? 0 : 4}
                  className={`border-end bg-light d-flex flex-column${step === 0 ? ' d-none' : ''}`}
                  style={{ padding: '1.5rem 1.25rem' }}
                >
                  <div className='fw-semibold text-muted small text-uppercase mb-3 letter-spacing-1'>
                    Workflow Preview
                  </div>

                  {/* Start node */}
                  <div
                    className='rounded px-3 py-2 mb-1 text-white small fw-semibold text-center'
                    style={{ background: '#3d7a6f', maxWidth: 220, alignSelf: 'center' }}
                  >
                    ⚡ {triggerLabel}
                  </div>

                  {/* Connector */}
                  <div className='d-flex justify-content-center'>
                    <div style={{ width: 2, height: 24, background: '#ccc' }} />
                  </div>

                  {/* Action node */}
                  <div
                    className='border rounded px-3 py-2 bg-white small position-relative'
                    style={{ maxWidth: 220, alignSelf: 'center', minWidth: 180 }}
                  >
                    <span
                      className='badge bg-primary position-absolute top-0 end-0 me-1'
                      style={{ marginTop: -10, fontSize: 10 }}
                    >
                      Day {form.delayDays}
                    </span>
                    <div className='fw-semibold'>
                      {form.channel === 'EMAIL' ? '✉️ Send Email' : '💬 Send SMS'}
                    </div>
                    {selectedTemplate ? (
                      <div className='text-muted' style={{ fontSize: 11 }}>
                        {selectedTemplate.name}
                      </div>
                    ) : (
                      <div className='text-warning' style={{ fontSize: 11 }}>
                        No template selected
                      </div>
                    )}
                  </div>

                  {/* Connector to end */}
                  <div className='d-flex justify-content-center'>
                    <div style={{ width: 2, height: 24, background: '#ccc' }} />
                  </div>

                  {/* End node */}
                  <div
                    className='rounded px-3 py-2 text-muted small text-center border border-dashed'
                    style={{ maxWidth: 220, alignSelf: 'center', borderStyle: 'dashed !important', fontSize: 11 }}
                  >
                    End of workflow
                  </div>

                  {/* Config summary */}
                  {form.name && (
                    <div className='mt-auto pt-3 border-top'>
                      <div className='small text-muted'><strong>Name:</strong> {form.name}</div>
                      {form.description && (
                        <div className='small text-muted mt-1' style={{ fontSize: 11 }}>
                          {form.description}
                        </div>
                      )}
                    </div>
                  )}
                </Col>

                {/* ── Wizard Content ── */}
                <Col md={step === 0 ? 12 : 8} style={{ padding: '1.5rem' }}>

                {/* ─── Step 0: Template Library ─── */}
                {step === 0 && (
                    <div>
                        <div className='d-flex justify-content-between align-items-center mb-3'>
                            <h6 className='mb-0 fw-bold'>Choose a starting point</h6>
                            <Button variant='outline-secondary' size='sm' onClick={() => { setForm(DEFAULT_STATE); setStep(1) }}>
                                Start from scratch
                            </Button>
                        </div>

                        {/* Search + Category filter */}
                        <div className='d-flex gap-2 mb-3 flex-wrap'>
                            <input
                                type='text'
                                className='form-control form-control-sm'
                                style={{ maxWidth: 200 }}
                                placeholder='Search templates...'
                                value={librarySearch}
                                onChange={(e) => setLibrarySearch(e.target.value)}
                            />
                            <div className='d-flex gap-1 flex-wrap'>
                                {AUTOMATION_STARTER_CATEGORIES.map((cat) => (
                                    <button
                                        key={cat.value}
                                        type='button'
                                        className={`btn btn-sm ${libraryCategory === cat.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => setLibraryCategory(cat.value)}
                                    >
                                        {cat.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div style={{ maxHeight: 420, overflowY: 'auto', paddingRight: 4 }}>

                            {/* Custom templates section */}
                            {filteredCustomTemplates.length > 0 && (
                                <div className='mb-3'>
                                    <div className='text-muted small fw-semibold mb-2 text-uppercase'>My Templates</div>
                                    {filteredCustomTemplates.map((t) => (
                                        <div key={t.id} className='border rounded p-3 mb-2 d-flex align-items-start gap-3'>
                                            <div className='flex-grow-1'>
                                                <div className='fw-semibold small d-flex align-items-center gap-2'>
                                                    {t.name}
                                                    <Badge bg='secondary' style={{ fontSize: 10 }}>{t.category}</Badge>
                                                </div>
                                                {t.description && <div className='text-muted' style={{ fontSize: 12 }}>{t.description}</div>}
                                                <div className='text-muted' style={{ fontSize: 11 }}>
                                                    {t.channel} · {t.triggerType} · Day {t.delayDays}
                                                </div>
                                            </div>
                                            <div className='d-flex gap-1 flex-shrink-0'>
                                                <Button size='sm' variant='primary' onClick={() => applyStarterToForm(t)}>Use</Button>
                                                <Button
                                                    size='sm'
                                                    variant='outline-danger'
                                                    disabled={deletingTemplateId === t.id}
                                                    onClick={() => handleDeleteCustomTemplate(t.id)}
                                                >
                                                    {deletingTemplateId === t.id ? <Spinner animation='border' size='sm' /> : '✕'}
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* System templates section */}
                            {filteredSystemStarters.length > 0 && (
                                <div>
                                    <div className='text-muted small fw-semibold mb-2 text-uppercase'>
                                        Recommended Templates
                                    </div>
                                    {filteredSystemStarters.map((s) => {
                                        const alreadySaved = customTemplates.some((t) => t.name === s.name)
                                        return (
                                            <div key={s.id} className='border rounded p-3 mb-2 d-flex align-items-start gap-3'>
                                                <div className='flex-grow-1'>
                                                    <div className='fw-semibold small d-flex align-items-center gap-2'>
                                                        {s.name}
                                                        <Badge bg='light' text='dark' style={{ fontSize: 10 }}>{s.category}</Badge>
                                                    </div>
                                                    <div className='text-muted' style={{ fontSize: 12 }}>{s.description}</div>
                                                    <div className='text-muted' style={{ fontSize: 11 }}>
                                                        {s.channel} · Day {s.delayDays}
                                                    </div>
                                                </div>
                                                <div className='d-flex gap-1 flex-shrink-0'>
                                                    <Button size='sm' variant='primary' onClick={() => applyStarterToForm(s)}>Use</Button>
                                                    <Button
                                                        size='sm'
                                                        variant={alreadySaved ? 'outline-secondary' : 'outline-success'}
                                                        disabled={savingTemplateId === s.id || alreadySaved}
                                                        title={alreadySaved ? 'Already in your library' : 'Save to my templates'}
                                                        onClick={() => handleSaveAsCustomTemplate(s)}
                                                    >
                                                        {savingTemplateId === s.id
                                                            ? <Spinner animation='border' size='sm' />
                                                            : alreadySaved ? '✓' : '+ Save'
                                                        }
                                                    </Button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}

                            {filteredSystemStarters.length === 0 && filteredCustomTemplates.length === 0 && (
                                <div className='text-muted text-center py-4 small'>No templates match your search.</div>
                            )}
                        </div>
                    </div>
                )}

                {/* Progress — only show for steps 1-3 */}
                {step > 0 && (
                <div className='mb-4'>
                    <div className='d-flex justify-content-between small text-muted mb-2'>
                        <span className={step === 1 ? 'fw-bold text-primary' : ''}>1. Trigger</span>
                        <span className={step === 2 ? 'fw-bold text-primary' : ''}>2. Channel + Template</span>
                        <span className={step === 3 ? 'fw-bold text-primary' : ''}>3. Configuration</span>
                    </div>
                    <ProgressBar now={progress} style={{ height: 4 }} />
                </div>
                )}

                {/* ─── Step 1: Trigger ─── */}
                {step === 1 && (
                    <div>
                        <h6 className='mb-3'>🎯 What event triggers this automation?</h6>

                        <Tabs
                            activeKey={activeTab}
                            onSelect={(k) => setActiveTab(k || 'general')}
                            className='mb-3'
                        >
                            <Tab eventKey='general' title='General'>
                                <Row xs={1} sm={2} className='g-2'>
                                    {TRIGGER_OPTIONS.filter((t) => ['HAPPY_BIRTHDAY'].includes(t.value)).map((trigger) => (
                                        <Col key={trigger.value}>
                                            <div
                                                className={`border rounded p-3 h-100 ${form.triggerType === trigger.value
                                                    ? 'border-primary bg-primary bg-opacity-10'
                                                    : 'hover-shadow'
                                                    }`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => set('triggerType', trigger.value)}
                                            >
                                                <div className='fw-semibold'>{trigger.label}</div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Tab>
                            <Tab eventKey='leads' title='Leads'>
                                <Row xs={1} sm={2} className='g-2'>
                                    {TRIGGER_OPTIONS.filter((t) => ['CONDITIONAL_LEAD'].includes(t.value)).map((trigger) => (
                                        <Col key={trigger.value}>
                                            <div
                                                className={`border rounded p-3 h-100 ${form.triggerType === trigger.value
                                                    ? 'border-primary bg-primary bg-opacity-10'
                                                    : 'hover-shadow'
                                                    }`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => set('triggerType', trigger.value)}
                                            >
                                                <div className='fw-semibold'>{trigger.label}</div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Tab>
                            <Tab eventKey='policy' title='Policy / Quote'>
                                <Row xs={1} sm={2} className='g-2'>
                                    {TRIGGER_OPTIONS.filter((t) => ['CONDITIONAL_POLICY'].includes(t.value)).map((trigger) => (
                                        <Col key={trigger.value}>
                                            <div
                                                className={`border rounded p-3 h-100 ${form.triggerType === trigger.value
                                                    ? 'border-primary bg-primary bg-opacity-10'
                                                    : 'hover-shadow'
                                                    }`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => set('triggerType', trigger.value)}
                                            >
                                                <div className='fw-semibold'>{trigger.label}</div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Tab>
                        </Tabs>

                        {/* Config for CONDITIONAL_LEAD */}
                        {form.triggerType === 'CONDITIONAL_LEAD' && (
                            <div className='mt-4 p-4 border rounded bg-light'>
                                <h6 className='fw-semibold mb-3'>Lead Condition Settings</h6>
                                <Form.Group className='mb-3'>
                                    <Form.Label>Condition Rule</Form.Label>
                                    <Form.Select
                                        value={form.triggerConfig?.condition_rule || ''}
                                        onChange={(e) => set('triggerConfig', { ...form.triggerConfig, condition_rule: e.target.value })}
                                    >
                                        <option value=''>Select a rule...</option>
                                        {LEAD_CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </Form.Select>
                                </Form.Group>

                                {form.triggerConfig?.condition_rule === 'LEAD_DURATION' && (
                                    <Form.Group>
                                        <Form.Label>Duration (Days)</Form.Label>
                                        <Form.Control
                                            type='number'
                                            min='0'
                                            value={form.triggerConfig?.duration_days || 0}
                                            onChange={(e) => set('triggerConfig', { ...form.triggerConfig, duration_days: parseInt(e.target.value) || 0 })}
                                        />
                                        <Form.Text className='text-muted'>Contacts whose lead was created exactly this many days ago.</Form.Text>
                                    </Form.Group>
                                )}

                                {form.triggerConfig?.condition_rule === 'LEAD_STAGE' && (
                                    <Row className='g-3'>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Stage Name</Form.Label>
                                                <Form.Control
                                                    type='text'
                                                    placeholder='e.g. Qualified, In Progress...'
                                                    value={form.triggerConfig?.lead_stage || ''}
                                                    onChange={(e) => set('triggerConfig', { ...form.triggerConfig, lead_stage: e.target.value })}
                                                />
                                                <Form.Text className='text-muted'>Partial match on your pipeline stage name.</Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Days ago updated</Form.Label>
                                                <Form.Control
                                                    type='number'
                                                    min='0'
                                                    value={form.triggerConfig?.duration_days || 0}
                                                    onChange={(e) => set('triggerConfig', { ...form.triggerConfig, duration_days: parseInt(e.target.value) || 0 })}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}
                            </div>
                        )}

                        {/* Config for CONDITIONAL_POLICY */}
                        {form.triggerType === 'CONDITIONAL_POLICY' && (
                            <div className='mt-4 p-4 border rounded bg-light'>
                                <h6 className='fw-semibold mb-3'>Policy/Quote Condition Settings</h6>
                                <Form.Group className='mb-3'>
                                    <Form.Label>Condition Rule</Form.Label>
                                    <Form.Select
                                        value={form.triggerConfig?.condition_rule || ''}
                                        onChange={(e) => set('triggerConfig', { ...form.triggerConfig, condition_rule: e.target.value })}
                                    >
                                        <option value=''>Select a rule...</option>
                                        {POLICY_CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </Form.Select>
                                </Form.Group>

                                {(form.triggerConfig?.condition_rule === 'STATE_CHANGE' ||
                                  form.triggerConfig?.condition_rule === 'CROSS_SELL_AUTO' ||
                                  form.triggerConfig?.condition_rule === 'CROSS_SELL_HOME' ||
                                  form.triggerConfig?.condition_rule === 'POLICY_COMPLETED') && (
                                    <Form.Group className='mt-3'>
                                        <Form.Label>Duration in this state (Days)</Form.Label>
                                        <Form.Control
                                            type='number'
                                            min='0'
                                            value={form.triggerConfig?.duration_days || 0}
                                            onChange={(e) => set('triggerConfig', { ...form.triggerConfig, duration_days: parseInt(e.target.value) || 0 })}
                                        />
                                    </Form.Group>
                                )}

                                {form.triggerConfig?.condition_rule === 'BY_CARRIER' && (
                                    <Form.Group className='mt-3'>
                                        <Form.Label>Carrier Name</Form.Label>
                                        <Form.Control
                                            type='text'
                                            placeholder='Enter carrier name...'
                                            value={form.triggerConfig?.carrier || ''}
                                            onChange={(e) => set('triggerConfig', { ...form.triggerConfig, carrier: e.target.value })}
                                        />
                                    </Form.Group>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ─── Step 2: Canal + Template ─── */}
                {step === 2 && (
                    <div>
                        <h6 className='mb-3'>📨 Through which channel and with what template?</h6>

                        <Form.Group className='mb-4'>
                            <Form.Label className='fw-semibold'>Sending channel</Form.Label>
                            <div className='d-flex gap-3'>
                                {(['EMAIL', 'SMS'] as const).map((ch) => (
                                    <div
                                        key={ch}
                                        className={`border rounded px-4 py-3 text-center ${form.channel === ch
                                            ? 'border-primary bg-primary bg-opacity-10 fw-bold'
                                            : ''
                                            }`}
                                        style={{ cursor: 'pointer', minWidth: 120 }}
                                        onClick={() => { set('channel', ch); set('templateId', '') }}
                                    >
                                        {ch === 'EMAIL' ? '✉️ Email' : '💬 SMS'}
                                    </div>
                                ))}
                            </div>
                        </Form.Group>

                        <Form.Group>
                            <div className='d-flex justify-content-between align-items-center mb-1'>
                                <Form.Label className='fw-semibold mb-0'>Template</Form.Label>
                                <Button variant='link' size='sm' className='p-0 text-decoration-none' onClick={handleOpenCreateTemplate}>
                                    + Create Template
                                </Button>
                            </div>
                            {loadingTemplates ? (
                                <div className='text-muted'><Spinner animation='border' size='sm' /> Loading...</div>
                            ) : templates.length === 0 ? (
                                <div className='alert alert-warning small py-2'>
                                    No templates available for <strong>{form.channel}</strong>.
                                    Create one in the Templates module and return here.
                                </div>
                            ) : (
                                <div style={{ maxHeight: 280, overflowY: 'auto', paddingRight: 4 }}>
                                    <Row xs={1} sm={2} className='g-2'>
                                        {templates.map((t) => (
                                            <Col key={t.id}>
                                                <div
                                                    className={`border rounded p-3 h-100 position-relative ${form.templateId === t.id
                                                        ? 'border-primary bg-primary bg-opacity-10'
                                                        : ''
                                                        }`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => set('templateId', t.id)}
                                                >
                                                    <div className='fw-semibold small pe-5'>{t.name}</div>
                                                    {t.subject && (
                                                        <div className='text-muted' style={{ fontSize: 12 }}>
                                                            Subject: {t.subject}
                                                        </div>
                                                    )}
                                                    <button
                                                        type='button'
                                                        className='btn btn-sm btn-light position-absolute top-0 end-0 m-1 p-1'
                                                        style={{ lineHeight: 1, zIndex: 1 }}
                                                        title='Preview'
                                                        onClick={(e) => { e.stopPropagation(); setPreviewTemplate(t) }}
                                                    >
                                                        <i className='bi bi-eye' style={{ fontSize: 14 }}></i>
                                                    </button>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            )}
                        </Form.Group>
                    </div>
                )}

                {/* ─── Step 3: Configuración ─── */}
                {step === 3 && (
                    <div>
                        <h6 className='mb-3'>⚙️ Final configuration</h6>
                        <Row>
                            <Col md={8}>
                                <Form.Group className='mb-3'>
                                    <Form.Label className='fw-semibold'>Name *</Form.Label>
                                    <Form.Control
                                        value={form.name}
                                        onChange={(e) => set('name', e.target.value)}
                                        placeholder='Ex: 30 days expiration reminder'
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className='mb-3'>
                                    <Form.Label className='fw-semibold'>Status</Form.Label>
                                    <div className='d-flex align-items-center gap-2 mt-1'>
                                        <Form.Check
                                            type='switch'
                                            id='auto-active-switch'
                                            label={form.isActive ? 'Active' : 'Inactive'}
                                            checked={form.isActive}
                                            onChange={(e) => set('isActive', e.target.checked)}
                                        />
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        {isAdmin && (
                            <Row>
                                <Col md={12}>
                                    <Form.Group className='mb-3'>
                                        <Form.Label className='fw-semibold'>Scope</Form.Label>
                                        <Form.Select
                                            value={form.scope}
                                            onChange={(e) => set('scope', e.target.value as 'ORGANIZATION' | 'PERSONAL')}
                                        >
                                            <option value='PERSONAL'>Personal (Assigned to me)</option>
                                            <option value='ORGANIZATION'>Organization Wide</option>
                                        </Form.Select>
                                        <Form.Text className='text-muted'>
                                            Determine if this automation applies to all contacts in the organization or only those assigned to you.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        <Form.Group className='mb-3'>
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as='textarea'
                                rows={2}
                                value={form.description}
                                onChange={(e) => set('description', e.target.value)}
                                placeholder='Optional description...'
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className='mb-3'>
                                    <Form.Label>⏳ Delay (days after trigger)</Form.Label>
                                    <Form.Control
                                        type='number'
                                        min={0}
                                        max={365}
                                        value={form.delayDays}
                                        onChange={(e) => set('delayDays', parseInt(e.target.value) || 0)}
                                    />
                                    <Form.Text>0 = send immediately</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className='mb-3'>
                                    <Form.Label>
                                        <i className='bi bi-clock me-1'></i>Preferred sending time
                                    </Form.Label>
                                    <Form.Control
                                        type='time'
                                        value={form.sendTime}
                                        onChange={(e) => set('sendTime', e.target.value)}
                                    />
                                    <Form.Text className='text-muted'>Hour and minutes</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Summary */}
                        <div className='p-3 border rounded bg-light-primary mt-2 small text-gray-800'>
                            <i className='bi bi-clipboard-check me-2 text-primary'></i>
                            <strong>Summary:</strong>{' '}
                            Trigger <strong>{TRIGGER_OPTIONS.find(t => t.value === form.triggerType)?.label}</strong>
                            {(form.triggerType === 'CONDITIONAL_LEAD' || form.triggerType === 'CONDITIONAL_POLICY') && form.triggerConfig?.condition_rule && (
                                <span className='text-muted'> ({form.triggerConfig.condition_rule})</span>
                            )}
                            {' → '}<strong>{form.channel}</strong>
                            {' → '}Template <strong>{templates.find(t => t.id === form.templateId)?.name || <span className='text-danger'>unselected</span>}</strong>
                            {form.delayDays > 0 && <span className='text-muted'> · {form.delayDays} day delay</span>}
                        </div>
                    </div>
                )}
                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer className='d-flex flex-column gap-0 p-0'>
                {/* Eligible contacts banner */}
                {step === 3 && (
                    <div className='w-100 px-4 py-2 border-bottom bg-light d-flex align-items-center gap-2 small'>
                        <i className='bi bi-people text-primary'></i>
                        <span className='text-muted'>
                            {form.triggerType === 'HAPPY_BIRTHDAY' ? 'Contacts with birthday registered:' : 'Eligible contacts today:'}
                        </span>
                        {previewLoading ? (
                            <Spinner animation='border' size='sm' />
                        ) : (
                            <strong className={eligibleCount === 0 ? 'text-warning' : 'text-success'}>
                                {eligibleCount ?? '—'}
                            </strong>
                        )}
                        {!previewLoading && eligibleCount === 0 && (
                            <span className='text-muted ms-1'>— no contacts match these conditions today</span>
                        )}
                    </div>
                )}
                <div className='d-flex justify-content-between w-100 px-4 py-3'>
                    <div>
                        {step > 1 && (
                            <Button variant='outline-secondary' onClick={() => setStep(s => s - 1)}>
                                <i className='bi bi-arrow-left me-1'></i>Back
                            </Button>
                        )}
                        {step === 1 && !automation && (
                            <Button variant='outline-secondary' onClick={() => setStep(0)}>
                                <i className='bi bi-arrow-left me-1'></i>Templates
                            </Button>
                        )}
                    </div>
                    <div className='d-flex gap-2'>
                        <Button variant='outline-secondary' onClick={onHide}>Cancel</Button>
                        {step === 0 ? null : step < 3 ? (
                            <Button variant='primary' onClick={() => setStep(s => s + 1)}>
                                Next <i className='bi bi-arrow-right ms-1'></i>
                            </Button>
                        ) : (
                            <Button
                                variant='success'
                                onClick={handleSave}
                                disabled={creating || updating}
                            >
                                {(creating || updating) && <Spinner animation='border' size='sm' className='me-1' />}
                                {automation ? 'Save changes' : 'Create automation'}
                            </Button>
                        )}
                    </div>
                </div>
            </Modal.Footer>
        </Modal>

        {/* Template Creation Modals */}
        <TemplateStarterModal
            show={starterModalOpen}
            onHide={() => setStarterModalOpen(false)}
            onSelect={handleSelectStarter}
            onStartFromScratch={handleStartFromScratch}
        />
        <TemplateFormModal
            show={templateModalOpen}
            onHide={() => setTemplateModalOpen(false)}
            onSubmit={handleCreateTemplateSubmit}
            starter={selectedStarter}
            loading={creatingTemplate}
            defaultChannel={defaultChannel}
        />

        {/* Template Preview Modal */}
        <Modal show={!!previewTemplate} onHide={() => setPreviewTemplate(null)} size='lg' centered>
            <Modal.Header closeButton>
                <Modal.Title className='fs-6'>
                    <i className='bi bi-eye me-2 text-primary'></i>
                    {previewTemplate?.name}
                    {previewTemplate?.subject && (
                        <span className='text-muted fw-normal ms-2' style={{ fontSize: 13 }}>
                            — {previewTemplate.subject}
                        </span>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className='p-0'>
                {previewTemplate && (() => {
                    const variant = previewTemplate.variants?.find(v => v.isDefault) ?? previewTemplate.variants?.[0]
                    const html = (variant as any)?.bodyHtml
                    const text = (variant as any)?.bodyText ?? previewTemplate.body
                    if (html) {
                        return (
                            <div
                                style={{ maxHeight: '70vh', overflowY: 'auto', padding: '1rem' }}
                                dangerouslySetInnerHTML={{ __html: html }}
                            />
                        )
                    }
                    return (
                        <pre className='p-3 m-0' style={{ maxHeight: '70vh', overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: 13 }}>
                            {text || 'No content available'}
                        </pre>
                    )
                })()}
            </Modal.Body>
            <Modal.Footer className='py-2'>
                <Button
                    variant='primary'
                    size='sm'
                    onClick={() => { set('templateId', previewTemplate!.id); setPreviewTemplate(null) }}
                >
                    <i className='bi bi-check2 me-1'></i>Use this template
                </Button>
                <Button variant='outline-secondary' size='sm' onClick={() => setPreviewTemplate(null)}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
        </>
    )
}
